import { HttpApiService } from "@/shared/services/httpApi.service";

/** The payment events the backend can notify; each is also the preference key that switches it on. */
export type PaymentNotificationEvent =
  | "payin_confirmed"
  | "payin_rejected"
  | "payout_paid"
  | "payout_failed"
  | "wallet_balance_low"
  | "batch_pending_approval"
  | "batch_finished"
  | "breb_payin_confirmed"
  | "breb_payout_paid"
  | "breb_payout_failed"
  | "breb_qr_payment_paid";

/** The payments module of the organization's `notification_preferences`. */
export type PaymentNotificationPreferences = Record<PaymentNotificationEvent, boolean> & {
  /** E.164; null sends to `default_phone`. */
  phone: string | null;
  wallet_balance_low_thresholds: Record<string, number>;
};

/** What GET and PATCH answer: the preferences plus the number `phone: null` sends to. */
export type PaymentNotificationPreferencesView = PaymentNotificationPreferences & {
  /** The phone of the organization's own verification, else its registered phone; null when it has neither. */
  default_phone: string | null;
};

export interface PaymentNotificationTestResult {
  sent: boolean;
  /** `skipped` means the messaging service has that notification switched off. */
  status: "sent" | "already_sent" | "skipped" | null;
}

const basePath = (organizationId: string) =>
  `/internal/organizations/${organizationId}/notification-preferences/payments`;

export const paymentNotificationsService = {
  get: async (organizationId: string): Promise<PaymentNotificationPreferencesView> => {
    const response = await HttpApiService.get<PaymentNotificationPreferencesView>(basePath(organizationId));
    return response.data;
  },

  update: async (
    organizationId: string,
    prefs: Partial<PaymentNotificationPreferences>,
  ): Promise<PaymentNotificationPreferencesView> => {
    const response = await HttpApiService.patch<
      PaymentNotificationPreferencesView,
      Partial<PaymentNotificationPreferences>
    >(basePath(organizationId), prefs);
    return response.data;
  },

  sendTest: async (
    organizationId: string,
    event: PaymentNotificationEvent,
    phone: string,
  ): Promise<PaymentNotificationTestResult> => {
    const response = await HttpApiService.post<
      PaymentNotificationTestResult,
      { event: PaymentNotificationEvent; phone: string }
    >(`${basePath(organizationId)}/test`, { event, phone });
    return response.data;
  },
};
